import { useEffect, useState, useCallback } from "react";
import { Search, Download, Filter, ChevronLeft, ChevronRight, Info, Clock, User, Target as TargetIcon, Shield, Zap } from "lucide-react";
import { useAuth, API } from "../../context/AuthContext";

// Platform-only labels — everything a Sys Admin can trigger + system alerts.
// Kept intentionally small; anything unknown falls back to the raw slug.
const ACTION_LABELS = {
  admin_login: "Sys Admin Login",
  accountant_created: "Accountant Created",
  accountant_deleted: "Accountant Deleted",
  merchant_created: "Merchant Created",
  merchant_deleted: "Merchant Deleted",
  shop_admin_created: "Shop Admin Created",
  shop_admin_deleted: "Shop Admin Deleted",
  finance_admin_created: "Finance Admin Created",
  finance_admin_deleted: "Finance Admin Deleted",
  payment_charges_updated: "Payment Charges Updated",
  alert_new_ip_admin_login: "Alert · New IP Admin Login",
  alert_rapid_state_change: "Alert · Rapid State Change",
  platform_audit_exported: "Platform Audit Exported",
};
const prettyAction = (a) => ACTION_LABELS[a] || (a || "").replaceAll("_", " ");

function formatBerlin(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", { timeZone: "Europe/Berlin", hour12: false });
  } catch { return iso; }
}
function formatValue(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (typeof v === "number") return String(v);
  if (Array.isArray(v)) return v.length === 0 ? "—" : v.join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function ActivityCard({ row }) {
  const hasDetails = row.details && Object.keys(row.details).length > 0;
  const isAlert = (row.action || "").startsWith("alert_");
  return (
    <div className={`rounded-2xl border p-3 hover:border-slate-300 transition-colors ${isAlert ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium shrink-0 flex items-center gap-1 ${isAlert ? "bg-amber-100 text-amber-800" : "bg-indigo-50 text-indigo-700"}`}>
          {isAlert && <Zap className="w-3 h-3" />} {prettyAction(row.action)}
        </span>
        <span className="text-[11px] text-slate-500 flex items-center gap-1 shrink-0">
          <Clock className="w-3 h-3" /> {formatBerlin(row.created_at)}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-700">
        <div className="flex items-baseline gap-1.5">
          <User className="w-3 h-3 text-slate-400 shrink-0" />
          <span className="truncate">
            <span className="font-medium">{row.actor_name || row.actor_email || "—"}</span>
            {row.actor_name && row.actor_email && (
              <span className="text-slate-400 text-[10.5px] ml-1">{row.actor_email}</span>
            )}
          </span>
        </div>
        <div className="flex items-baseline gap-1.5">
          <Shield className="w-3 h-3 text-slate-400 shrink-0" />
          <span className="text-slate-500">{row.actor_role || "—"}</span>
          {row.ip_address && <span className="text-[10.5px] text-slate-400 font-mono ml-2">{row.ip_address}</span>}
        </div>
        {(row.target_type || row.target_id) && (
          <div className="flex items-baseline gap-1.5 sm:col-span-2">
            <TargetIcon className="w-3 h-3 text-slate-400 shrink-0" />
            <span className="text-slate-700">{row.target_type || "—"}</span>
            {row.target_id && (
              <span className="text-[10.5px] text-slate-400 font-mono ml-1 break-all">{row.target_id}</span>
            )}
          </div>
        )}
      </div>
      {hasDetails && (
        <div className="mt-2 pt-2 border-t border-dashed border-slate-200 flex flex-wrap gap-1">
          {Object.entries(row.details).map(([k, v]) => (
            <span key={k}
              className="inline-flex items-baseline gap-1 max-w-full rounded-md bg-white border border-slate-200 px-1.5 py-0.5 text-[10.5px] leading-tight"
              title={`${k}: ${formatValue(v)}`}>
              <span className="text-slate-500 font-medium">{k}:</span>
              <span className="text-slate-800 truncate max-w-[280px]">{formatValue(v)}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PlatformActivity() {
  const { authAxios } = useAuth();
  const [q, setQ] = useState("");
  const [action, setAction] = useState("");
  const [role, setRole] = useState("");
  const [actor, setActor] = useState("");
  const [targetType, setTargetType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (q) params.q = q;
      if (action) params.action = action;
      if (role) params.role = role;
      if (actor) params.actor = actor;
      if (targetType) params.target_type = targetType;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await authAxios.get("/admin/platform-audit", { params });
      setData(res.data);
    } catch (err) { console.error("Platform audit fetch failed:", err?.message || err); }
    finally { setLoading(false); }
  }, [q, action, role, actor, targetType, startDate, endDate, page, pageSize, authAxios]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { setPage(1); }, [q, action, role, actor, targetType, startDate, endDate]);

  const download = async (fmt) => {
    const params = new URLSearchParams({ fmt });
    if (q) params.set("q", q);
    if (action) params.set("action", action);
    if (role) params.set("role", role);
    if (actor) params.set("actor", actor);
    if (targetType) params.set("target_type", targetType);
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    const res = await fetch(`${API}/admin/platform-audit/export?${params.toString()}`, { credentials: "include" });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `platform-audit.${fmt}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setQ(""); setAction(""); setRole(""); setActor(""); setTargetType(""); setStartDate(""); setEndDate("");
  };
  const facets = data?.facets || { actions: [], roles: [], target_types: [] };
  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;
  const items = data?.items || [];

  return (
    <div data-testid="platform-activity-section">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <div>
          <h2 className="text-xl font-bold text-[#1e3a5f] flex items-center gap-2">
            <Shield className="w-5 h-5" /> Platform Activity
          </h2>
          <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
            <Info className="w-3 h-3" /> Sys Admin, system alerts, and platform-only actions — the trail Finance Admins never see.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => download("csv")} className="px-3 py-1.5 rounded-full text-sm bg-white border border-slate-200 hover:bg-slate-50 flex items-center gap-1" data-testid="platform-export-csv-btn">
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
          <button onClick={() => download("xlsx")} className="px-3 py-1.5 rounded-full text-sm bg-[#1e3a5f] text-white hover:bg-[#152845] flex items-center gap-1" data-testid="platform-export-xlsx-btn">
            <Download className="w-3.5 h-3.5" /> Excel
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-3 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search actions, actors, targets…"
            className="w-full pl-9 pr-3 py-2 rounded-full border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#8b5cf6]"
            data-testid="platform-search-input" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
          <select value={action} onChange={(e) => setAction(e.target.value)} className="px-2 py-1.5 text-sm rounded border border-slate-200 bg-white" data-testid="platform-filter-action">
            <option value="">All actions</option>
            {facets.actions.map(a => <option key={a} value={a}>{prettyAction(a)}</option>)}
          </select>
          <select value={role} onChange={(e) => setRole(e.target.value)} className="px-2 py-1.5 text-sm rounded border border-slate-200 bg-white" data-testid="platform-filter-role">
            <option value="">All roles</option>
            {facets.roles.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <select value={targetType} onChange={(e) => setTargetType(e.target.value)} className="px-2 py-1.5 text-sm rounded border border-slate-200 bg-white" data-testid="platform-filter-target">
            <option value="">All targets</option>
            {facets.target_types.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <input value={actor} onChange={(e) => setActor(e.target.value)} placeholder="Actor email"
            className="px-2 py-1.5 text-sm rounded border border-slate-200" data-testid="platform-filter-actor" />
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
            className="px-2 py-1.5 text-sm rounded border border-slate-200" data-testid="platform-filter-start-date" />
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
            className="px-2 py-1.5 text-sm rounded border border-slate-200" data-testid="platform-filter-end-date" />
          <button onClick={clearFilters} className="col-span-2 sm:col-span-2 px-3 py-1.5 text-xs rounded-full border border-slate-200 bg-slate-50 hover:bg-slate-100 flex items-center justify-center gap-1" data-testid="platform-clear-filters-btn">
            <Filter className="w-3 h-3" /> Clear filters
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {loading && <div className="text-center text-slate-400 py-6 text-sm">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="text-center text-slate-400 py-6 text-sm bg-white rounded-2xl border border-slate-200" data-testid="platform-empty">
            No platform activity matches your filters.
          </div>
        )}
        {!loading && items.map(row => <ActivityCard key={row.id} row={row} />)}
        <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border border-slate-200 rounded-2xl text-xs text-slate-500">
          <span data-testid="platform-total">
            {data ? `${data.total} entr${data.total === 1 ? "y" : "ies"}` : "—"}
          </span>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}
              className="p-1 rounded hover:bg-white disabled:opacity-40" data-testid="platform-prev-page">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span>Page {page} of {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              className="p-1 rounded hover:bg-white disabled:opacity-40" data-testid="platform-next-page">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
